import Phaser from 'phaser';
import { GRASS, DIRT, TOWER_COLORS, ENEMY_COLORS } from './theme';
import type { TowerType, EnemyType } from '../core/types';

const TOWER_TYPES: TowerType[] = [
  'patty',
  'pickle',
  'cheese',
  'bacon',
  'sauce',
  'blade',
  'bomb',
  'wise',
  'wizard',
  'sniper',
  'darkhambi',
  'ninja',
  'sensei',
  'boxer',
  'gunner',
  'firemage',
  'aquasorcerer',
  'shrekbook',
  'phantom',
];
const ENEMY_TYPES: EnemyType[] = ['grunt', 'dino', 'fry', 'hotdog', 'boss'];

/** Size constants for placeholder textures. */
const TOWER_SIZE = 48;
const ENEMY_SIZE = 32;
const PLOT_SIZE = 64;
const PROJ_SIZE = 16;
const DECAL_SIZE = 64;

/**
 * Generate placeholder textures for every art key used in the game.
 * Call once inside BootScene.create() before starting any other scene.
 * Each Graphics object is destroyed immediately after generateTexture() to
 * keep memory tidy.
 *
 * Texture keys produced:
 *   tower-<type>-<tier>   (tower-patty-1, tower-patty-2, …)
 *   enemy-<type>          (enemy-grunt, …)
 *   plot
 *   bg-forest
 *   dirt
 */
export function makePlaceholderTextures(scene: Phaser.Scene): void {
  // -- Towers: filled square; tier-2 white border, tier-3 gold border + X --
  for (const type of TOWER_TYPES) {
    for (const tier of [1, 2, 3] as const) {
      const key = `tower-${type}-${tier}`;
      // Skip if a real texture was already loaded for this key.
      if (scene.textures.exists(key)) continue;
      const color = TOWER_COLORS[type];
      const gfx = scene.add.graphics();
      gfx.fillStyle(color, 1);
      gfx.fillRect(0, 0, TOWER_SIZE, TOWER_SIZE);
      // tier-1 dark border, tier-2 white border, tier-3 gold border.
      const borderColor = tier === 3 ? 0xf5c518 : tier === 2 ? 0xffffff : 0x000000;
      gfx.lineStyle(tier === 3 ? 4 : 3, borderColor, 1);
      gfx.strokeRect(1, 1, TOWER_SIZE - 2, TOWER_SIZE - 2);
      // Inner stripe(s) mark the tier: one diagonal for tier-2, an X for tier-3.
      if (tier >= 2) {
        gfx.lineStyle(2, tier === 3 ? 0xf5c518 : 0xffffff, 0.6);
        gfx.lineBetween(4, TOWER_SIZE - 4, TOWER_SIZE - 4, 4);
        if (tier === 3) gfx.lineBetween(4, 4, TOWER_SIZE - 4, TOWER_SIZE - 4);
      }
      gfx.generateTexture(key, TOWER_SIZE, TOWER_SIZE);
      gfx.destroy();
    }
  }

  // -- Evolution (tier-4) towers: emerald border + diamond mark. Only the six
  //    evolvable types have a 4th tier (see config/unlocks.ts EVOLVE_TOWERS). --
  for (const type of ['bomb', 'ninja', 'wise', 'sensei', 'cheese', 'patty']) {
    const key = `tower-${type}-4`;
    if (scene.textures.exists(key)) continue;
    const color = TOWER_COLORS[type as keyof typeof TOWER_COLORS];
    const gfx = scene.add.graphics();
    gfx.fillStyle(color, 1);
    gfx.fillRect(0, 0, TOWER_SIZE, TOWER_SIZE);
    gfx.lineStyle(4, 0x2ee06a, 1);
    gfx.strokeRect(1, 1, TOWER_SIZE - 2, TOWER_SIZE - 2);
    // Diamond mark — reads as "evolved" even at 60px.
    const c = TOWER_SIZE / 2;
    gfx.lineStyle(2, 0x2ee06a, 0.8);
    gfx.strokePoints(
      [
        { x: c, y: 8 },
        { x: TOWER_SIZE - 8, y: c },
        { x: c, y: TOWER_SIZE - 8 },
        { x: 8, y: c },
        { x: c, y: 8 },
      ],
      true,
    );
    gfx.generateTexture(key, TOWER_SIZE, TOWER_SIZE);
    gfx.destroy();
  }

  // -- Enemies: filled circle, white outline --
  for (const type of ENEMY_TYPES) {
    const key = `enemy-${type}`;
    // Skip if a real texture was already loaded for this key.
    if (scene.textures.exists(key)) continue;
    const color = ENEMY_COLORS[type];
    const radius = ENEMY_SIZE / 2;
    const gfx = scene.add.graphics();
    gfx.fillStyle(color, 1);
    gfx.fillCircle(radius, radius, radius);
    gfx.lineStyle(2, 0xffffff, 0.8);
    gfx.strokeCircle(radius, radius, radius - 1);
    gfx.generateTexture(key, ENEMY_SIZE, ENEMY_SIZE);
    gfx.destroy();
  }

  // -- Plot: outlined square with semi-transparent fill (≥44px) --
  if (!scene.textures.exists('plot')) {
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x000000, 0.25);
    gfx.fillRect(0, 0, PLOT_SIZE, PLOT_SIZE);
    gfx.lineStyle(3, 0xffffff, 0.8);
    gfx.strokeRect(2, 2, PLOT_SIZE - 4, PLOT_SIZE - 4);
    // Inner dashed-look: short lines at corners
    gfx.lineStyle(2, 0xffffff, 0.4);
    gfx.lineBetween(8, 8, 16, 8);
    gfx.lineBetween(8, 8, 8, 16);
    gfx.lineBetween(PLOT_SIZE - 8, 8, PLOT_SIZE - 16, 8);
    gfx.lineBetween(PLOT_SIZE - 8, 8, PLOT_SIZE - 8, 16);
    gfx.lineBetween(8, PLOT_SIZE - 8, 16, PLOT_SIZE - 8);
    gfx.lineBetween(8, PLOT_SIZE - 8, 8, PLOT_SIZE - 16);
    gfx.lineBetween(PLOT_SIZE - 8, PLOT_SIZE - 8, PLOT_SIZE - 16, PLOT_SIZE - 8);
    gfx.lineBetween(PLOT_SIZE - 8, PLOT_SIZE - 8, PLOT_SIZE - 8, PLOT_SIZE - 16);
    gfx.generateTexture('plot', PLOT_SIZE, PLOT_SIZE);
    gfx.destroy();
  }

  // -- bg-forest: full grass-coloured fill at canvas size --
  if (!scene.textures.exists('bg-forest')) {
    const gfx = scene.add.graphics();
    gfx.fillStyle(GRASS, 1);
    gfx.fillRect(0, 0, 1280, 720);
    gfx.generateTexture('bg-forest', 1280, 720);
    gfx.destroy();
  }

  // -- bg-mountains: Chapter 2 · Fluffy Peak. Plain pale-snow fallback only —
  //    the real art is the AI-generated `bg-mountains` (see tools/asset-manifest.ts;
  //    run `npm run assets`), which overrides this once present. Kept minimal so
  //    the level never shows a missing texture before that art is generated. --
  if (!scene.textures.exists('bg-mountains')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0xe8f1fb, 1); // pale snow, fills the whole frame
    gfx.fillRect(0, 0, W, H);
    // Light cool edge shading so it doesn't read as a flat empty rectangle.
    gfx.fillStyle(0xc7dbef, 0.5);
    gfx.fillRect(0, 0, W, 80);
    gfx.fillRect(0, H - 80, W, 80);
    gfx.fillRect(0, 0, 80, H);
    gfx.fillRect(W - 80, 0, 80, H);
    gfx.generateTexture('bg-mountains', W, H);
    gfx.destroy();
  }

  // -- bg-igloo: Chapter 2 · The Icy Igloos. Pale-snow fallback with a few
  //    simple igloo domes at the frame edges — the real art is the
  //    AI-generated `bg-igloo` (see tools/asset-manifest.ts; run
  //    `npm run assets`), which overrides this once present. --
  if (!scene.textures.exists('bg-igloo')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0xeaf3fc, 1); // pale snow, fills the whole frame
    gfx.fillRect(0, 0, W, H);
    // Light cool edge shading so it doesn't read as a flat empty rectangle.
    gfx.fillStyle(0xc7dbef, 0.5);
    gfx.fillRect(0, 0, W, 80);
    gfx.fillRect(0, H - 80, W, 80);
    gfx.fillRect(0, 0, 80, H);
    gfx.fillRect(W - 80, 0, 80, H);
    // Simple igloo domes with doorways along the edges so the theme reads
    // before the real art is generated.
    const domes: Array<[number, number, number]> = [
      [140, 66, 48],
      [640, 56, 54],
      [1150, 70, 46],
      [220, 700, 50],
      [1010, 702, 52],
    ];
    for (const [x, y, r] of domes) {
      gfx.fillStyle(0xffffff, 1);
      gfx.fillCircle(x, y, r);
      gfx.fillStyle(0x9fc2e0, 1);
      gfx.fillCircle(x, y + r * 0.55, r * 0.32); // arched doorway
    }
    gfx.generateTexture('bg-igloo', W, H);
    gfx.destroy();
  }

  // -- bg-gemcave: Chapter 2 · Shady Business. Dark cave-floor fallback with
  //    purple/blue crystal clusters at the frame edges — the real art is the
  //    AI-generated `bg-gemcave` (see tools/asset-manifest.ts; run
  //    `npm run assets`), which overrides this once present. --
  if (!scene.textures.exists('bg-gemcave')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x2b2436, 1); // dark violet-grey cave floor
    gfx.fillRect(0, 0, W, H);
    // Deeper shadow around the edges so the centre reads as the open floor.
    gfx.fillStyle(0x1c1726, 0.7);
    gfx.fillRect(0, 0, W, 80);
    gfx.fillRect(0, H - 80, W, 80);
    gfx.fillRect(0, 0, 80, H);
    gfx.fillRect(W - 80, 0, 80, H);
    // Purple and blue crystal clusters along the edges.
    const crystals: Array<[number, number, number, number]> = [
      [120, 60, 34, 0x9b59d0],
      [430, 52, 26, 0x3b6fd4],
      [820, 58, 30, 0x9b59d0],
      [1180, 64, 36, 0x3b6fd4],
      [70, 660, 32, 0x3b6fd4],
      [520, 674, 26, 0x9b59d0],
      [990, 668, 34, 0x9b59d0],
    ];
    for (const [x, y, r, c] of crystals) {
      gfx.fillStyle(c, 0.95);
      gfx.fillTriangle(x, y - r, x + r * 0.7, y + r * 0.6, x - r * 0.7, y + r * 0.6);
      gfx.fillStyle(0xffffff, 0.35);
      gfx.fillTriangle(x, y - r * 0.9, x + r * 0.25, y + r * 0.4, x - r * 0.1, y + r * 0.4);
    }
    gfx.generateTexture('bg-gemcave', W, H);
    gfx.destroy();
  }

  // -- bg-shop: the Shop's kitchen backdrop. Simple procedural kitchen —
  //    tiled wall, shelf, counter — until the real AI-generated bg-shop.png
  //    exists (see tools/asset-manifest.ts; run `npm run assets`). The scene
  //    dims it with an overlay, so it only needs to read as "kitchen". --
  if (!scene.textures.exists('bg-shop')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    // Warm plaster wall.
    gfx.fillStyle(0x8a6a4a, 1);
    gfx.fillRect(0, 0, W, H);
    // Tiled backsplash band across the middle.
    gfx.fillStyle(0xa8886a, 1);
    gfx.fillRect(0, 180, W, 320);
    gfx.lineStyle(3, 0x7a5c40, 0.8);
    for (let x = 0; x <= W; x += 80) gfx.lineBetween(x, 180, x, 500);
    for (let y = 180; y <= 500; y += 80) gfx.lineBetween(0, y, W, y);
    // Shelf near the top with simple pot/jar silhouettes.
    gfx.fillStyle(0x5c3a1e, 1);
    gfx.fillRect(0, 130, W, 22);
    gfx.fillStyle(0x4a3020, 1);
    for (let x = 90; x < W; x += 190) {
      gfx.fillRoundedRect(x, 84, 60, 46, 6); // pot body
      gfx.fillRect(x + 14, 74, 32, 12); // lid
    }
    // Wooden counter along the bottom, with a warm oven glow.
    gfx.fillStyle(0x6b4526, 1);
    gfx.fillRect(0, 500, W, H - 500);
    gfx.lineStyle(3, 0x4a3020, 0.9);
    for (let y = 560; y < H; y += 70) gfx.lineBetween(0, y, W, y);
    gfx.fillStyle(0xf5a028, 0.35);
    gfx.fillRoundedRect(W / 2 - 130, 545, 260, 130, 10);
    gfx.generateTexture('bg-shop', W, H);
    gfx.destroy();
  }

  // -- bg-evolve: the Evolve lab — green alchemy chamber with potion flasks
  //    along the floor. Placeholder until assets/atlas/bg-evolve.png exists. --
  if (!scene.textures.exists('bg-evolve')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    // Deep green lab wall with a darker vignette band up top.
    gfx.fillStyle(0x0d2b1a, 1);
    gfx.fillRect(0, 0, W, H);
    gfx.fillStyle(0x123c24, 1);
    gfx.fillRect(0, 120, W, 400);
    // Faint circuitry/pipe lines for a lab feel.
    gfx.lineStyle(2, 0x1f6b3c, 0.5);
    for (let x = 80; x < W; x += 160) gfx.lineBetween(x, 0, x, 120);
    gfx.lineStyle(3, 0x1f6b3c, 0.6);
    gfx.lineBetween(0, 120, W, 120);
    // Dark floor.
    gfx.fillStyle(0x0a1f12, 1);
    gfx.fillRect(0, 520, W, H - 520);
    gfx.lineStyle(2, 0x143020, 0.8);
    for (let y = 560; y < H; y += 55) gfx.lineBetween(0, y, W, y);
    // Glowing potion flasks scattered on the floor.
    const flasks = [
      { x: 120, c: 0x39ff88 },
      { x: 320, c: 0x9b5aff },
      { x: 610, c: 0x39ff88 },
      { x: 900, c: 0x39d5ff },
      { x: 1150, c: 0x39ff88 },
    ];
    for (const f of flasks) {
      gfx.fillStyle(f.c, 0.25); // glow pool
      gfx.fillEllipse(f.x, 668, 90, 26);
      gfx.fillStyle(0x1a3a28, 1); // bottle body
      gfx.fillRoundedRect(f.x - 16, 618, 32, 44, 8);
      gfx.fillRect(f.x - 6, 604, 12, 18); // neck
      gfx.fillStyle(f.c, 0.85); // liquid
      gfx.fillRoundedRect(f.x - 12, 634, 24, 24, 6);
      gfx.fillStyle(0x2a1408, 1); // cork
      gfx.fillRect(f.x - 7, 598, 14, 8);
    }
    gfx.generateTexture('bg-evolve', W, H);
    gfx.destroy();
  }

  // -- bg-hambicastle: Chapter 3 · Episode I. Grey cobblestone floor framed by
  //    castle battlements, with a burger banner flying at the top — a minimal
  //    fallback until the AI-generated `bg-hambicastle` (see
  //    tools/asset-manifest.ts; run `npm run assets`) overrides it. --
  if (!scene.textures.exists('bg-hambicastle')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    // Cobblestone floor.
    gfx.fillStyle(0x9a9a94, 1);
    gfx.fillRect(0, 0, W, H);
    // Cooler stone edge frame so it doesn't read as a flat rectangle.
    gfx.fillStyle(0x74746e, 1);
    gfx.fillRect(0, 0, W, 80);
    gfx.fillRect(0, H - 80, W, 80);
    gfx.fillRect(0, 0, 80, H);
    gfx.fillRect(W - 80, 0, 80, H);
    // Crenellated battlement blocks along the top edge.
    gfx.fillStyle(0x5f5f59, 1);
    for (let x = 20; x < W; x += 96) gfx.fillRect(x, 0, 56, 44);
    // Corner turrets with blue conical roofs.
    for (const cx of [56, W - 56]) {
      gfx.fillStyle(0x6b6b64, 1);
      gfx.fillRect(cx - 34, 0, 68, 120);
      gfx.fillStyle(0x3f6fb0, 1);
      gfx.fillTriangle(cx - 40, 40, cx + 40, 40, cx, -18);
    }
    // Burger banner on a flagpole near the top centre.
    gfx.fillStyle(0x4a3020, 1);
    gfx.fillRect(W / 2 - 2, 20, 4, 150); // pole
    gfx.fillStyle(0xc0392b, 1);
    gfx.fillRect(W / 2 + 2, 26, 96, 62); // banner cloth
    // Simple burger emblem: bun, patty, lettuce.
    const bx = W / 2 + 50;
    gfx.fillStyle(0xe0a24a, 1);
    gfx.fillRoundedRect(bx - 26, 36, 52, 16, 8); // top bun
    gfx.fillStyle(0x2ecc71, 1);
    gfx.fillRect(bx - 26, 52, 52, 6); // lettuce
    gfx.fillStyle(0x6b3f24, 1);
    gfx.fillRect(bx - 24, 58, 48, 8); // patty
    gfx.fillStyle(0xe0a24a, 1);
    gfx.fillRoundedRect(bx - 26, 66, 52, 10, 5); // bottom bun
    gfx.generateTexture('bg-hambicastle', W, H);
    gfx.destroy();
  }

  // -- bg-hambivillage: Chapter 3 · Episode II. A moonlit field framed by
  //    thatched huts — most of them in a row along the TOP edge, matching the
  //    AI-generated art. A minimal fallback until the real `bg-hambivillage`
  //    (see tools/asset-manifest.ts; run `npm run assets`) overrides it.
  //    Huts stay clear of level 17's trail, which holds y >= 210 (see
  //    levels.ts id 17), so nothing here is drawn under the runtime trail. --
  if (!scene.textures.exists('bg-hambivillage')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    // Open meadow, after dark.
    gfx.fillStyle(0x2c5560, 1);
    gfx.fillRect(0, 0, W, H);
    // Deeper night grass framing the edges.
    gfx.fillStyle(0x24454f, 1);
    gfx.fillRect(0, 0, W, 165);
    gfx.fillRect(0, H - 70, W, 70);
    gfx.fillRect(0, 0, 90, H);
    gfx.fillRect(W - 90, 0, 90, H);
    // The moon, low in the night sky above the hut row.
    gfx.fillStyle(0xdfe6c8, 1);
    gfx.fillCircle(640, 34, 26);
    // Huts: a packed row across the top, plus the bottom corners. Rough timber
    // walls under a shaggy straw roof — deliberately not tidy cottages.
    const huts: [number, number][] = [
      [70, 96],
      [210, 96],
      [350, 96],
      [490, 96],
      [790, 96],
      [930, 96],
      [1070, 96],
      [1210, 96],
      [62, H - 40],
      [1218, H - 40],
    ];
    for (const [cx, cy] of huts) {
      gfx.fillStyle(0x5b4636, 1);
      gfx.fillRect(cx - 42, cy - 18, 84, 70); // timber walls
      gfx.fillStyle(0x8a7a4e, 1);
      gfx.fillTriangle(cx - 54, cy - 18, cx + 54, cy - 18, cx, cy - 68); // thatch
      gfx.fillStyle(0x3b2f24, 1);
      gfx.fillRect(cx - 11, cy + 18, 22, 34); // plank door
      gfx.fillStyle(0xf2b453, 1); // windows lit from within
      gfx.fillRect(cx - 32, cy - 4, 15, 15);
      gfx.fillRect(cx + 17, cy - 4, 15, 15);
    }
    gfx.generateTexture('bg-hambivillage', W, H);
    gfx.destroy();
  }

  // -- bg-gloomyforest: Chapter 3 · Episode III. A murky forest floor framed by
  //    dark trees, with a river down the right edge and a fog wash over the
  //    whole frame — a minimal fallback until the AI-generated
  //    `bg-gloomyforest` (see tools/asset-manifest.ts; run `npm run assets`)
  //    overrides it. --
  if (!scene.textures.exists('bg-gloomyforest')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    // Damp forest floor.
    gfx.fillStyle(0x4a5b46, 1);
    gfx.fillRect(0, 0, W, H);
    // Gloomier undergrowth framing the edges.
    gfx.fillStyle(0x36452f, 1);
    gfx.fillRect(0, 0, W, 80);
    gfx.fillRect(0, H - 80, W, 80);
    gfx.fillRect(0, 0, 80, H);
    gfx.fillRect(W - 80, 0, 80, H);
    // River hugging the right edge (never crossing the open centre).
    gfx.fillStyle(0x3d5a6c, 1);
    gfx.fillRect(W - 46, 0, 46, H);
    gfx.fillStyle(0x51748a, 1);
    for (let y = 30; y < H; y += 90) gfx.fillRect(W - 40, y, 34, 6); // ripples
    // Dark trunks crowding the top and bottom edges.
    gfx.fillStyle(0x2e2620, 1);
    for (let x = 30; x < W - 60; x += 110) {
      gfx.fillRect(x, 0, 18, 62);
      gfx.fillRect(x + 46, H - 58, 18, 58);
    }
    // Fog: pale drifting bands over the whole frame.
    gfx.fillStyle(0xb9c6cc, 0.16);
    for (let y = 90; y < H - 60; y += 120) gfx.fillRect(0, y, W, 46);
    gfx.fillStyle(0xcdd8dd, 0.1);
    gfx.fillRect(0, 0, W, H);
    gfx.generateTexture('bg-gloomyforest', W, H);
    gfx.destroy();
  }

  // -- enemy-frost: the "regular nugget" (enemy-grunt) art, snow-covered.
  //    Derived from the loaded grunt bitmap — an icy wash kept inside the
  //    nugget's silhouette (source-atop) plus a white snow cap — so it reads as
  //    the same nugget with a frosty makeover. Falls back to an icy blob if the
  //    grunt art isn't available. (Same derive-from-bitmap trick as the coin.) --
  if (!scene.textures.exists('enemy-frost')) {
    if (scene.textures.exists('enemy-grunt')) {
      const gruntImg = scene.textures.get('enemy-grunt').getSourceImage() as CanvasImageSource & {
        width: number;
        height: number;
      };
      const w = gruntImg.width || ENEMY_SIZE;
      const h = gruntImg.height || ENEMY_SIZE;
      const tex = scene.textures.createCanvas('enemy-frost', w, h);
      if (tex) {
        const ctx = tex.getContext();
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(gruntImg, 0, 0);
        // Light icy wash — confined to the nugget's own pixels. Kept subtle so
        // the underlying nugget reads clearly (only "a bit" frosted).
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = 'rgba(150,210,240,0.3)';
        ctx.fillRect(0, 0, w, h);
        // A modest snow cap + one small dab — less snow coverage than before.
        ctx.fillStyle = 'rgba(255,255,255,0.82)';
        ctx.beginPath();
        ctx.ellipse(w * 0.5, h * 0.23, w * 0.36, h * 0.15, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(w * 0.62, h * 0.46, w * 0.05, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
        tex.refresh();
        scene.textures.get('enemy-frost').setFilter(Phaser.Textures.FilterMode.NEAREST);
      }
    }
    // Fallback: nugget-toned blob with a modest snow cap (only lightly frosted).
    if (!scene.textures.exists('enemy-frost')) {
      const radius = ENEMY_SIZE / 2;
      const gfx = scene.add.graphics();
      gfx.fillStyle(ENEMY_COLORS.frost, 1);
      gfx.fillCircle(radius, radius, radius);
      gfx.fillStyle(0xffffff, 0.75);
      gfx.fillCircle(radius, radius * 0.62, radius * 0.42);
      gfx.lineStyle(2, 0xffffff, 0.7);
      gfx.strokeCircle(radius, radius, radius - 1);
      gfx.generateTexture('enemy-frost', ENEMY_SIZE, ENEMY_SIZE);
      gfx.destroy();
    }
  }

  // -- enemy-icedino: the Dino Scout (enemy-dino) art sealed inside an ice
  //    cube. Derived from the loaded dino bitmap — an icy wash over the dino
  //    plus a translucent bevelled cube drawn around it — so it reads as the
  //    same dino frozen mid-run. Falls back to a cyan cube blob if the dino
  //    art isn't available. (Same derive-from-bitmap trick as enemy-frost.) --
  if (!scene.textures.exists('enemy-icedino')) {
    if (scene.textures.exists('enemy-dino')) {
      const dinoImg = scene.textures.get('enemy-dino').getSourceImage() as CanvasImageSource & {
        width: number;
        height: number;
      };
      const w = dinoImg.width || ENEMY_SIZE;
      const h = dinoImg.height || ENEMY_SIZE;
      const tex = scene.textures.createCanvas('enemy-icedino', w, h);
      if (tex) {
        const ctx = tex.getContext();
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, w, h);
        // The dino, slightly inset so the cube walls fit around it.
        const inset = w * 0.1;
        ctx.drawImage(dinoImg, inset, inset, w - inset * 2, h - inset * 2);
        // Blue-tint the dino's own pixels (seen through the ice).
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = 'rgba(120,200,235,0.35)';
        ctx.fillRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
        // The cube: translucent icy fill + bright bevelled frame + glint.
        const m = Math.max(1, Math.round(w * 0.03)); // cube margin
        ctx.fillStyle = 'rgba(170,220,245,0.4)';
        ctx.fillRect(m, m, w - m * 2, h - m * 2);
        ctx.strokeStyle = 'rgba(235,250,255,0.95)';
        ctx.lineWidth = Math.max(2, Math.round(w * 0.05));
        ctx.strokeRect(m, m, w - m * 2, h - m * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(w * 0.14, h * 0.14, w * 0.16, h * 0.06); // top-left glint
        ctx.fillRect(w * 0.14, h * 0.22, w * 0.06, h * 0.1);
        tex.refresh();
        scene.textures.get('enemy-icedino').setFilter(Phaser.Textures.FilterMode.NEAREST);
      }
    }
    // Fallback: a translucent ice cube with a green dino silhouette inside.
    if (!scene.textures.exists('enemy-icedino')) {
      const S = ENEMY_SIZE;
      const gfx = scene.add.graphics();
      gfx.fillStyle(ENEMY_COLORS.icedino, 0.55);
      gfx.fillRect(1, 1, S - 2, S - 2);
      gfx.fillStyle(ENEMY_COLORS.dino, 0.9); // the dino blob inside
      gfx.fillCircle(S / 2, S * 0.58, S * 0.28);
      gfx.fillCircle(S * 0.68, S * 0.4, S * 0.14); // head
      gfx.lineStyle(3, 0xeafaff, 0.95);
      gfx.strokeRect(1, 1, S - 2, S - 2);
      gfx.generateTexture('enemy-icedino', S, S);
      gfx.destroy();
    }
  }

  // -- enemy-driller: the Hotdog Brute (enemy-hotdog) art with a gemstone
  //    makeover — a faceted purple crystal "drill" head and a blue gem on the
  //    body, drawn over the loaded hotdog bitmap. Falls back to a purple
  //    crystal blob if the hotdog art isn't available. (Same derive-from-bitmap
  //    trick as enemy-frost / enemy-icedino.) --
  if (!scene.textures.exists('enemy-driller')) {
    if (scene.textures.exists('enemy-hotdog')) {
      const dogImg = scene.textures.get('enemy-hotdog').getSourceImage() as CanvasImageSource & {
        width: number;
        height: number;
      };
      const w = dogImg.width || ENEMY_SIZE;
      const h = dogImg.height || ENEMY_SIZE;
      const tex = scene.textures.createCanvas('enemy-driller', w, h);
      if (tex) {
        const ctx = tex.getContext();
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(dogImg, 0, 0);
        // Heavy amethyst wash confined to the brute's own pixels — the whole
        // driller reads as shades of purple.
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = 'rgba(150,90,220,0.55)';
        ctx.fillRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
        // Purple crystal drill replacing the entire head — a big faceted cone
        // covering the whole head area, tip up.
        ctx.fillStyle = 'rgba(160,108,224,0.95)';
        ctx.beginPath();
        ctx.moveTo(w * 0.5, h * 0.02); // drill tip
        ctx.lineTo(w * 0.82, h * 0.48);
        ctx.lineTo(w * 0.18, h * 0.48);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = 'rgba(220,190,255,0.9)'; // facet highlight
        ctx.beginPath();
        ctx.moveTo(w * 0.5, h * 0.05);
        ctx.lineTo(w * 0.62, h * 0.45);
        ctx.lineTo(w * 0.42, h * 0.45);
        ctx.closePath();
        ctx.fill();
        // Dark purple gem set on the body.
        const gx = w * 0.5;
        const gy = h * 0.62;
        const r = w * 0.11;
        ctx.fillStyle = 'rgba(94,42,168,0.95)';
        ctx.beginPath();
        ctx.moveTo(gx, gy - r);
        ctx.lineTo(gx + r, gy);
        ctx.lineTo(gx, gy + r);
        ctx.lineTo(gx - r, gy);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = 'rgba(215,185,255,0.9)';
        ctx.fillRect(gx - r * 0.35, gy - r * 0.45, r * 0.5, r * 0.4); // glint
        tex.refresh();
        scene.textures.get('enemy-driller').setFilter(Phaser.Textures.FilterMode.NEAREST);
      }
    }
    // Fallback: purple crystal-headed blob with a dark purple chest gem.
    if (!scene.textures.exists('enemy-driller')) {
      const S = ENEMY_SIZE;
      const gfx = scene.add.graphics();
      gfx.fillStyle(ENEMY_COLORS.driller, 1);
      gfx.fillCircle(S / 2, S * 0.6, S * 0.32); // body
      gfx.fillStyle(0xc9a6f2, 1);
      gfx.fillTriangle(S * 0.5, S * 0.02, S * 0.8, S * 0.48, S * 0.2, S * 0.48); // drill = whole head
      gfx.fillStyle(0x5e2aa8, 1);
      gfx.fillCircle(S / 2, S * 0.62, S * 0.12); // dark purple body gem
      gfx.generateTexture('enemy-driller', S, S);
      gfx.destroy();
    }
  }

  // -- enemy-nullnugget: the Golden Nugget boss (enemy-boss) art gone dark — a
  //    heavy charcoal wash confined to the nugget's pixels, angry grey brows
  //    and a black wizard hat drawn on top. Falls back to a grey blob with a
  //    hat if the boss art isn't available. (Same derive-from-bitmap trick as
  //    enemy-frost / enemy-icedino / enemy-driller.) --
  if (!scene.textures.exists('enemy-nullnugget')) {
    if (scene.textures.exists('enemy-boss')) {
      const bossImg = scene.textures.get('enemy-boss').getSourceImage() as CanvasImageSource & {
        width: number;
        height: number;
      };
      const w = bossImg.width || ENEMY_SIZE;
      const h = bossImg.height || ENEMY_SIZE;
      const tex = scene.textures.createCanvas('enemy-nullnugget', w, h);
      if (tex) {
        const ctx = tex.getContext();
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(bossImg, 0, 0);
        // Void wash — desaturate the golden boss to charcoal-and-grey.
        ctx.globalCompositeOperation = 'saturation';
        ctx.fillStyle = 'rgb(0,0,0)';
        ctx.fillRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = 'rgba(30,30,40,0.55)';
        ctx.fillRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
        // Black wizard hat: brim + tall crooked cone over the head area.
        ctx.fillStyle = 'rgba(18,16,28,0.97)';
        ctx.beginPath();
        ctx.ellipse(w * 0.5, h * 0.22, w * 0.3, h * 0.06, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(w * 0.34, h * 0.22);
        ctx.lineTo(w * 0.66, h * 0.22);
        ctx.lineTo(w * 0.56, h * 0.03);
        ctx.closePath();
        ctx.fill();
        // A dim violet band on the hat so it reads "wizard", not "cone".
        ctx.fillStyle = 'rgba(120,80,200,0.85)';
        ctx.fillRect(w * 0.38, h * 0.185, w * 0.24, h * 0.035);
        tex.refresh();
        scene.textures.get('enemy-nullnugget').setFilter(Phaser.Textures.FilterMode.NEAREST);
      }
    }
    // Fallback: dark grey nugget blob with angry eyes and a black wizard hat.
    if (!scene.textures.exists('enemy-nullnugget')) {
      const S = ENEMY_SIZE;
      const gfx = scene.add.graphics();
      gfx.fillStyle(ENEMY_COLORS.nullnugget, 1);
      gfx.fillCircle(S / 2, S * 0.6, S * 0.34); // dark nugget body
      gfx.fillStyle(0xdddddd, 1); // angry eyes
      gfx.fillCircle(S * 0.42, S * 0.55, S * 0.05);
      gfx.fillCircle(S * 0.58, S * 0.55, S * 0.05);
      gfx.fillStyle(0x121016, 1); // wizard hat
      gfx.fillEllipse(S * 0.5, S * 0.34, S * 0.5, S * 0.1);
      gfx.fillTriangle(S * 0.34, S * 0.34, S * 0.66, S * 0.34, S * 0.56, S * 0.06);
      gfx.generateTexture('enemy-nullnugget', S, S);
      gfx.destroy();
    }
  }

  // -- enemy-ultranugget: a golden nugget studded with orange and red mine
  //    crystals — minimal placeholder until the real AI-generated
  //    enemy-ultranugget.png exists (see tools/asset-manifest.ts). --
  if (!scene.textures.exists('enemy-ultranugget')) {
    const S = ENEMY_SIZE;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0xc98a3b, 1); // golden-brown nugget body
    gfx.fillCircle(S / 2, S * 0.58, S * 0.32);
    gfx.fillStyle(ENEMY_COLORS.ultranugget, 1); // orange crystals
    gfx.fillTriangle(S * 0.3, S * 0.42, S * 0.44, S * 0.42, S * 0.37, S * 0.12);
    gfx.fillTriangle(S * 0.62, S * 0.5, S * 0.78, S * 0.5, S * 0.7, S * 0.24);
    gfx.fillStyle(0xd0342c, 1); // red crystals
    gfx.fillTriangle(S * 0.46, S * 0.4, S * 0.6, S * 0.4, S * 0.53, S * 0.08);
    gfx.fillTriangle(S * 0.16, S * 0.62, S * 0.3, S * 0.62, S * 0.23, S * 0.38);
    gfx.fillStyle(0x2d1c0d, 1); // determined eyes
    gfx.fillCircle(S * 0.42, S * 0.58, S * 0.04);
    gfx.fillCircle(S * 0.58, S * 0.58, S * 0.04);
    gfx.generateTexture('enemy-ultranugget', S, S);
    gfx.destroy();
  }

  // -- enemy-jockey: a dark cave spider with a nugget rider and spear —
  //    minimal placeholder until the real AI-generated enemy-jockey.png
  //    exists (see tools/asset-manifest.ts). --
  if (!scene.textures.exists('enemy-jockey')) {
    const S = ENEMY_SIZE;
    const gfx = scene.add.graphics();
    gfx.lineStyle(2, 0x3a2420, 1); // eight bent legs
    for (let i = 0; i < 4; i++) {
      const lx = S * (0.28 + i * 0.15);
      gfx.lineBetween(lx, S * 0.68, lx - S * 0.1, S * 0.94);
      gfx.lineBetween(lx, S * 0.68, lx + S * 0.1, S * 0.94);
    }
    gfx.fillStyle(ENEMY_COLORS.jockey, 1); // spider body
    gfx.fillEllipse(S * 0.5, S * 0.66, S * 0.6, S * 0.36);
    gfx.fillStyle(0xff3b30, 1); // glowing red eyes
    gfx.fillCircle(S * 0.72, S * 0.62, S * 0.045);
    gfx.fillCircle(S * 0.8, S * 0.66, S * 0.045);
    gfx.fillStyle(0xc98a3b, 1); // nugget rider on top
    gfx.fillCircle(S * 0.42, S * 0.36, S * 0.18);
    gfx.lineStyle(2, 0x8a6a3a, 1); // the rider's spear
    gfx.lineBetween(S * 0.52, S * 0.44, S * 0.9, S * 0.18);
    gfx.fillStyle(0xd9d2c5, 1); // spear tip
    gfx.fillTriangle(S * 0.9, S * 0.18, S * 0.84, S * 0.2, S * 0.88, S * 0.26);
    gfx.generateTexture('enemy-jockey', S, S);
    gfx.destroy();
  }

  // -- bg-nullcave: Chapter 2 finale · The Null Nugget. A cave so dark it eats
  //    light: near-black floor, the only warmth a ring of hanging lanterns
  //    framing the edges — until the real AI-generated bg-nullcave.png exists
  //    (see tools/asset-manifest.ts; run `npm run assets`). --
  if (!scene.textures.exists('bg-nullcave')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x121118, 1); // near-black cave floor
    gfx.fillRect(0, 0, W, H);
    // Even deeper shadow hugging the edges.
    gfx.fillStyle(0x0a090f, 0.8);
    gfx.fillRect(0, 0, W, 70);
    gfx.fillRect(0, H - 70, W, 70);
    gfx.fillRect(0, 0, 70, H);
    gfx.fillRect(W - 70, 0, 70, H);
    // Hanging lanterns around the frame: warm glow halo, body, tiny hook.
    const lanterns: Array<[number, number]> = [
      [110, 52],
      [420, 44],
      [760, 50],
      [1080, 46],
      [60, 300],
      [60, 560],
      [1220, 280],
      [1220, 540],
      [300, 680],
      [660, 688],
      [980, 682],
    ];
    for (const [x, y] of lanterns) {
      gfx.fillStyle(0xf5a028, 0.12); // soft glow halo
      gfx.fillCircle(x, y, 46);
      gfx.fillStyle(0xf5a028, 0.2);
      gfx.fillCircle(x, y, 26);
      gfx.fillStyle(0x3a2a12, 1); // lantern cage
      gfx.fillRoundedRect(x - 9, y - 13, 18, 26, 5);
      gfx.fillStyle(0xffd27f, 1); // flame window
      gfx.fillRoundedRect(x - 5, y - 8, 10, 16, 3);
      gfx.fillStyle(0x3a2a12, 1); // hook
      gfx.fillRect(x - 1.5, y - 19, 3, 6);
    }
    gfx.generateTexture('bg-nullcave', W, H);
    gfx.destroy();
  }

  // -- bg-cavern: Chapter 2 · Snowy Caverns. Simple procedural snow floor with
  //    cave-rock edges and roof icicles — until the real AI-generated
  //    bg-cavern.png exists (see tools/asset-manifest.ts; run `npm run assets`). --
  if (!scene.textures.exists('bg-cavern')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0xe4eef9, 1); // packed-snow floor fills the frame
    gfx.fillRect(0, 0, W, H);
    // Blue-grey cave rock framing the edges.
    gfx.fillStyle(0x8a9bb0, 0.7);
    gfx.fillRect(0, 0, W, 70);
    gfx.fillRect(0, H - 60, W, 60);
    gfx.fillRect(0, 0, 60, H);
    gfx.fillRect(W - 60, 0, 60, H);
    // Darker cavern depth in the upper corners.
    gfx.fillStyle(0x4a5a70, 0.6);
    gfx.fillTriangle(0, 0, 260, 0, 0, 150);
    gfx.fillTriangle(W, 0, W - 260, 0, W, 150);
    // Icicles hanging from the cave roof along the top edge.
    gfx.fillStyle(0xcfe6f5, 0.95);
    for (let x = 40; x < W; x += 90) {
      const len = 36 + ((x / 90) % 3) * 18; // 36/54/72 px, repeating
      gfx.fillTriangle(x, 70, x + 26, 70, x + 13, 70 + len);
    }
    gfx.generateTexture('bg-cavern', W, H);
    gfx.destroy();
  }

  // -- bg-mineshaft: Chapter 2 · The Mineshafts. Warm mine-dirt floor, timber
  //    braces and orange/red crystal clusters framing the edges — until the
  //    real AI-generated bg-mineshaft.png exists (see tools/asset-manifest.ts). --
  if (!scene.textures.exists('bg-mineshaft')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x4a3a28, 1); // packed mine-dirt floor
    gfx.fillRect(0, 0, W, H);
    gfx.fillStyle(0x2e2318, 0.85); // rock walls hugging the edges
    gfx.fillRect(0, 0, W, 64);
    gfx.fillRect(0, H - 64, W, 64);
    gfx.fillRect(0, 0, 64, H);
    gfx.fillRect(W - 64, 0, 64, H);
    // Timber support beams along the top and sides.
    gfx.fillStyle(0x6a4a26, 1);
    for (let x = 120; x < W - 80; x += 240) gfx.fillRect(x, 0, 26, 76);
    gfx.fillRect(0, 60, W, 14);
    // Orange and red crystal clusters studding the edge rock.
    const crystals: Array<[number, number, number]> = [
      [90, 120, 0xe8642c],
      [300, 46, 0xd0342c],
      [560, 40, 0xe8642c],
      [880, 48, 0xd0342c],
      [1180, 130, 0xe8642c],
      [46, 380, 0xd0342c],
      [50, 600, 0xe8642c],
      [1230, 420, 0xd0342c],
      [1226, 640, 0xe8642c],
      [400, 690, 0xd0342c],
      [800, 686, 0xe8642c],
    ];
    for (const [x, y, c] of crystals) {
      gfx.fillStyle(c, 0.25); // soft glow
      gfx.fillCircle(x, y, 34);
      gfx.fillStyle(c, 1);
      gfx.fillTriangle(x - 14, y + 12, x + 14, y + 12, x, y - 22);
      gfx.fillTriangle(x + 6, y + 12, x + 24, y + 12, x + 15, y - 8);
    }
    gfx.generateTexture('bg-mineshaft', W, H);
    gfx.destroy();
  }

  // -- bg-cobweb: Chapter 2 · The Cobweb Infestation. Grey cave floor, webbed
  //    edges and red eyes in the cracks — until the real AI-generated
  //    bg-cobweb.png exists (see tools/asset-manifest.ts). --
  if (!scene.textures.exists('bg-cobweb')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x3c3a40, 1); // grey-brown cave floor
    gfx.fillRect(0, 0, W, H);
    gfx.fillStyle(0x232128, 0.9); // dark rock edges
    gfx.fillRect(0, 0, W, 70);
    gfx.fillRect(0, H - 70, W, 70);
    gfx.fillRect(0, 0, 70, H);
    gfx.fillRect(W - 70, 0, 70, H);
    // Corner spiral webs: concentric arcs + radial strands.
    gfx.lineStyle(2, 0xd8d8e2, 0.5);
    for (const [cx, cy] of [
      [0, 0],
      [W, 0],
      [0, H],
      [W, H],
    ] as Array<[number, number]>) {
      for (const r of [60, 110, 160]) gfx.strokeCircle(cx, cy, r);
      const sx = cx === 0 ? 1 : -1;
      const sy = cy === 0 ? 1 : -1;
      for (const [dx, dy] of [
        [1, 0.15],
        [0.8, 0.8],
        [0.15, 1],
      ]) {
        gfx.lineBetween(cx, cy, cx + sx * dx * 170, cy + sy * dy * 170);
      }
    }
    // Web strands dangling from the roof.
    gfx.lineStyle(2, 0xd8d8e2, 0.4);
    for (let x = 200; x < W - 100; x += 180) gfx.lineBetween(x, 70, x + 12, 150);
    // Pairs of red eyes glinting from the edge walls.
    const eyes: Array<[number, number]> = [
      [350, 40],
      [700, 34],
      [1000, 44],
      [40, 300],
      [36, 520],
      [1244, 260],
      [1240, 560],
      [560, 690],
      [900, 688],
    ];
    gfx.fillStyle(0xff3b30, 0.95);
    for (const [x, y] of eyes) {
      gfx.fillCircle(x - 7, y, 4);
      gfx.fillCircle(x + 7, y, 4);
    }
    gfx.generateTexture('bg-cobweb', W, H);
    gfx.destroy();
  }

  // -- enemy-watcher: a big levitating amethyst crystal with a nugget riding
  //    it — minimal placeholder until the real AI-generated enemy-watcher.png
  //    exists (see tools/asset-manifest.ts). --
  if (!scene.textures.exists('enemy-watcher')) {
    const S = ENEMY_SIZE;
    const gfx = scene.add.graphics();
    // The crystal: a tall faceted amethyst diamond filling most of the frame.
    gfx.fillStyle(0x8a4fc8, 1); // deep amethyst body
    gfx.fillTriangle(S * 0.5, S * 0.98, S * 0.14, S * 0.52, S * 0.86, S * 0.52);
    gfx.fillTriangle(S * 0.14, S * 0.52, S * 0.86, S * 0.52, S * 0.5, S * 0.24);
    gfx.fillStyle(0xc9a0f0, 0.85); // lit facet
    gfx.fillTriangle(S * 0.5, S * 0.98, S * 0.14, S * 0.52, S * 0.5, S * 0.52);
    gfx.fillStyle(0xe6ccff, 0.9); // top glint
    gfx.fillTriangle(S * 0.38, S * 0.52, S * 0.5, S * 0.24, S * 0.5, S * 0.52);
    // The nugget rider perched on the crystal's tip.
    gfx.fillStyle(0xc98a3b, 1);
    gfx.fillCircle(S * 0.5, S * 0.16, S * 0.13);
    gfx.fillStyle(0x2d1c0d, 1); // determined eyes
    gfx.fillCircle(S * 0.45, S * 0.15, S * 0.03);
    gfx.fillCircle(S * 0.55, S * 0.15, S * 0.03);
    gfx.generateTexture('enemy-watcher', S, S);
    gfx.destroy();
  }

  // -- bg-amethyst: Chapter 2 · Amethyst Core. Dark violet geode floor with
  //    crystal-crusted, geode-ringed edges — until the real AI-generated
  //    bg-amethyst.png exists (see tools/asset-manifest.ts). --
  if (!scene.textures.exists('bg-amethyst')) {
    const W = 1280;
    const H = 720;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x241a33, 1); // dark violet cave floor
    gfx.fillRect(0, 0, W, H);
    gfx.fillStyle(0x160f22, 0.9); // near-black rock edges
    gfx.fillRect(0, 0, W, 64);
    gfx.fillRect(0, H - 64, W, 64);
    gfx.fillRect(0, 0, 64, H);
    gfx.fillRect(W - 64, 0, 64, H);
    // Geodes along the walls: dark stone rings with amethyst-lined hollows.
    const geodes: Array<[number, number, number]> = [
      [200, 34, 26],
      [560, 40, 30],
      [940, 32, 24],
      [36, 220, 28],
      [40, 470, 32],
      [1244, 180, 26],
      [1240, 420, 30],
      [1246, 620, 24],
      [320, 688, 28],
      [760, 692, 30],
      [1080, 686, 24],
    ];
    for (const [x, y, r] of geodes) {
      gfx.fillStyle(0x3a2f4a, 1); // stone shell
      gfx.fillCircle(x, y, r);
      gfx.fillStyle(0x6a3fa0, 1); // amethyst lining
      gfx.fillCircle(x, y, r * 0.7);
      gfx.fillStyle(0xc9a0f0, 0.9); // sparkling hollow
      gfx.fillCircle(x, y, r * 0.35);
    }
    // Crystal clusters jutting from the edge walls.
    gfx.fillStyle(0xa06ce0, 0.95);
    for (let x = 120; x < W - 60; x += 150) {
      gfx.fillTriangle(x, 64, x + 26, 64, x + 13, 18);
      gfx.fillTriangle(x + 30, H - 64, x + 56, H - 64, x + 43, H - 20);
    }
    for (let y = 130; y < H - 90; y += 160) {
      gfx.fillTriangle(64, y, 64, y + 26, 20, y + 13);
      gfx.fillTriangle(W - 64, y + 40, W - 64, y + 66, W - 20, y + 53);
    }
    gfx.generateTexture('bg-amethyst', W, H);
    gfx.destroy();
  }

  // -- dirt: 1×1 dirt-coloured tile (for path segments) --
  if (!scene.textures.exists('dirt')) {
    const gfx = scene.add.graphics();
    gfx.fillStyle(DIRT, 1);
    gfx.fillRect(0, 0, 1, 1);
    gfx.generateTexture('dirt', 1, 1);
    gfx.destroy();
  }

  // -- ui-panel-wood: warm-brown vertical wooden board for the right-panel HUD --
  if (!scene.textures.exists('ui-panel-wood')) {
    const W = 256;
    const H = 1024;
    const gfx = scene.add.graphics();

    // Base warm-brown fill
    gfx.fillStyle(0x8b5e3c, 1);
    gfx.fillRect(0, 0, W, H);

    // Horizontal plank seams (every ~100 px)
    gfx.lineStyle(3, 0x5c3a1e, 0.9);
    for (let y = 100; y < H; y += 100) {
      gfx.lineBetween(0, y, W, y);
    }

    // Subtle vertical grain lines (light)
    gfx.lineStyle(1, 0xa07850, 0.35);
    for (let x = 18; x < W; x += 28) {
      gfx.lineBetween(x, 0, x + 4, H);
    }

    // Darker grain accent lines
    gfx.lineStyle(1, 0x4a3020, 0.25);
    for (let x = 8; x < W; x += 42) {
      gfx.lineBetween(x, 0, x - 3, H);
    }

    // Darker carved border
    gfx.lineStyle(7, 0x3e2210, 1);
    gfx.strokeRect(3, 3, W - 6, H - 6);

    gfx.generateTexture('ui-panel-wood', W, H);
    gfx.destroy();
  }

  // -- diamond: the Shop's gem icon — faceted pixel gem, icy blue.
  //    Placeholder until a real assets/atlas/diamond.png is generated. --
  if (!scene.textures.exists('diamond')) {
    const S = 32;
    const gfx = scene.add.graphics();
    // Crown: bright top table flanked by darker side facets.
    gfx.fillStyle(0xdff1ff, 1);
    gfx.fillTriangle(10, 4, 22, 4, 16, 13);
    gfx.fillStyle(0x9ec9ff, 1);
    gfx.fillTriangle(10, 4, 16, 13, 3, 13);
    gfx.fillTriangle(22, 4, 29, 13, 16, 13);
    // Pavilion: the lower point, lit on the left.
    gfx.fillStyle(0x6fa8e8, 1);
    gfx.fillTriangle(3, 13, 29, 13, 16, 29);
    gfx.fillStyle(0x8fbcf2, 1);
    gfx.fillTriangle(3, 13, 16, 13, 16, 29);
    // Sparkle.
    gfx.fillStyle(0xffffff, 0.9);
    gfx.fillRect(12, 6, 2, 2);
    gfx.generateTexture('diamond', S, S);
    gfx.destroy();
    scene.textures.get('diamond').setFilter(Phaser.Textures.FilterMode.NEAREST);
  }

  // -- coin: a face texture + a frame-based flip spin (keys consumed by
  // spinCoin / COIN_SPIN_FRAMES in render/coin.ts) --
  // IMPORTANT: when a real assets/atlas/coin.png is bundled it is loaded as the
  // 'coin' key and this generator is skipped for the face — so the spin frames
  // MUST be derived from whatever 'coin' actually is (real art or the fallback
  // below), not assumed. We build them by horizontally squashing that bitmap, a
  // clean coin flip that stays consistent with the rest of the artwork.
  const COIN_SIZE = 64;

  // Fallback faces — only generated when no real <key>.png was loaded. The
  // Hambi coin is gold with an "H"; the Capycoin (the Capybara Event) is the
  // same coin in bronze-brown with a "C".
  const drawCoinFallback = (
    key: string,
    body: number,
    rim: number,
    shade: number,
    glyph: 'H' | 'C',
  ): void => {
    if (scene.textures.exists(key)) return;
    const CELL = 4;
    const GRID = 16;
    const c = (GRID - 1) / 2; // 7.5 — coin centre on the grid
    const gfx = scene.add.graphics();
    const isH = (gx: number, gy: number): boolean => {
      if (gy < 4 || gy > 11) return false;
      const bars = gx === 5 || gx === 6 || gx === 9 || gx === 10;
      const cross = gy >= 7 && gy <= 8 && gx >= 5 && gx <= 10;
      return bars || cross;
    };
    const isC = (gx: number, gy: number): boolean => {
      if (gy < 4 || gy > 11) return false;
      const spine = gx === 5 || gx === 6;
      const caps = (gy <= 5 || gy >= 10) && gx >= 5 && gx <= 10;
      return spine || caps;
    };
    const isGlyph = glyph === 'H' ? isH : isC;
    for (let gy = 0; gy < GRID; gy++) {
      for (let gx = 0; gx < GRID; gx++) {
        const dx = gx - c;
        const d2 = dx * dx + (gy - c) * (gy - c);
        if (d2 > 64) continue; // outside the disc → transparent
        let color = body;
        if (d2 > 39.7)
          color = rim; // dark rim ring
        else if (isGlyph(gx, gy))
          color = 0x3a2408; // dark glyph
        else if (dx + (gy - c) < -3.5)
          color = 0xffe888; // upper-left shine
        else if (dx + (gy - c) > 4.5) color = shade; // lower-right shade
        gfx.fillStyle(color, 1);
        gfx.fillRect(gx * CELL, gy * CELL, CELL, CELL);
      }
    }
    gfx.generateTexture(key, COIN_SIZE, COIN_SIZE);
    gfx.destroy();
    scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
  };
  drawCoinFallback('coin', 0xf5c518, 0x7a4f0a, 0xcf9c10, 'H');
  drawCoinFallback('capycoin', 0xa8703a, 0x4a2d10, 0x7a4f24, 'C');
  drawCoinFallback('darkcoin', 0x2b2d33, 0x0c0d10, 0x6b7080, 'H');
  drawCoinFallback('lightcoin', 0xeef4fb, 0x8a9bb0, 0xc6d4e4, 'H');

  // Spin frames: squash the actual 'coin' bitmap to progressively narrower
  // widths (a coin flipping edge-on), centred and full-height. NEAREST-sampled
  // so it stays crisp. Derived from the real face, so they always exist and
  // match it — fixing the missing-texture flashes spinCoin showed before.
  // The Capycoin and the Dark and Light Hambi Coins get the same treatment off
  // their own faces.
  for (const base of ['coin', 'capycoin', 'darkcoin', 'lightcoin']) {
    const faceImg = scene.textures.get(base).getSourceImage() as CanvasImageSource;
    const FRAMES: ReadonlyArray<readonly [string, number]> = [
      [`${base}-spin-2`, 0.62], // three-quarter turn
      [`${base}-spin-3`, 0.3], // edge-approaching
      [`${base}-spin-edge`, 0.14], // side-on edge
    ];
    for (const [key, frac] of FRAMES) {
      if (scene.textures.exists(key)) continue;
      const canvasTex = scene.textures.createCanvas(key, COIN_SIZE, COIN_SIZE);
      if (!canvasTex) continue;
      const ctx = canvasTex.getContext();
      ctx.imageSmoothingEnabled = false;
      const w = Math.max(4, Math.round(COIN_SIZE * frac));
      ctx.drawImage(faceImg, (COIN_SIZE - w) / 2, 0, w, COIN_SIZE);
      canvasTex.refresh();
      scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
    }
  }

  // -- The Capybara Event fallbacks (real art: tools/asset-manifest.ts) --
  // A capybara seen from above running toward the bottom of the frame: a
  // rounded body, the head at the bottom, four leg nubs whose forward pair
  // swaps between the two run frames. The red twins are the imposters.
  const drawCapy = (key: string, body: number, dark: number, frame: 1 | 2): void => {
    if (scene.textures.exists(key)) return;
    const S = 96;
    const gfx = scene.add.graphics();
    gfx.fillStyle(body, 1);
    gfx.fillRoundedRect(22, 18, 52, 56, 18);
    gfx.fillStyle(dark, 1);
    gfx.fillRoundedRect(30, 58, 36, 26, 10);
    gfx.fillRect(34, 56, 6, 6);
    gfx.fillRect(56, 56, 6, 6);
    const f = frame === 1 ? 0 : 8;
    gfx.fillRect(16, 26 + f, 8, 14);
    gfx.fillRect(72, 34 - f, 8, 14);
    gfx.fillRect(16, 54 - f, 8, 14);
    gfx.fillRect(72, 46 + f, 8, 14);
    gfx.generateTexture(key, S, S);
    gfx.destroy();
    scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
  };
  drawCapy('capy-run-1', 0xa8703a, 0x6b4321, 1);
  drawCapy('capy-run-2', 0xa8703a, 0x6b4321, 2);
  drawCapy('capy-red-1', 0xd23b2f, 0x7a1d15, 1);
  drawCapy('capy-red-2', 0xd23b2f, 0x7a1d15, 2);
  if (!scene.textures.exists('capsule-capy')) {
    const S = 128;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x7a4a24, 1);
    gfx.fillCircle(64, 64, 56);
    gfx.fillStyle(0x5a331a, 1);
    gfx.fillRect(8, 62, 112, 6);
    gfx.fillStyle(0xffffff, 0.35);
    gfx.fillCircle(44, 40, 12);
    gfx.generateTexture('capsule-capy', S, S);
    gfx.destroy();
  }
  for (const [key, color] of [
    ['bg-capyevent', 0x4f8a3a],
    ['bg-capyrun', 0x5c9a42],
    ['thumb-leaderboard', 0x0b1a33],
  ] as const) {
    if (scene.textures.exists(key)) continue;
    const gfx = scene.add.graphics();
    gfx.fillStyle(color, 1);
    gfx.fillRect(0, 0, 1280, 720);
    gfx.generateTexture(key, 1280, 720);
    gfx.destroy();
  }

  // -- Projectiles: small filled circle in the tower's accent colour --
  // Every tower that puts a projectile in the air needs an entry here, or a
  // missing `proj-<type>` key renders as Phaser's black missing-texture box.
  // (Shrekbook Hambi is exempt: it deliberately reuses proj-patty. Melee towers
  // fire nothing at all.)
  const PROJ_TOWERS: TowerType[] = [
    'patty',
    'pickle',
    'bacon',
    'sauce',
    'bomb',
    'wizard',
    'sniper',
    'darkhambi',
    'sensei',
    'gunner',
    'firemage',
    'aquasorcerer',
    'phantom',
    'lightning',
    'bow',
  ];
  for (const type of PROJ_TOWERS) {
    const key = `proj-${type}`;
    if (scene.textures.exists(key)) continue;
    const r = PROJ_SIZE / 2;
    const gfx = scene.add.graphics();
    gfx.fillStyle(TOWER_COLORS[type], 1);
    gfx.fillCircle(r, r, r);
    gfx.lineStyle(2, 0xffffff, 0.9);
    gfx.strokeCircle(r, r, r - 1);
    gfx.generateTexture(key, PROJ_SIZE, PROJ_SIZE);
    gfx.destroy();
  }

  // Bow Hambi's poison arrow — the same disc in toxic green, so a missing
  // proj-bow-poison.png never costs a texture.
  if (!scene.textures.exists('proj-bow-poison')) {
    const r = PROJ_SIZE / 2;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x7ccd3a, 1);
    gfx.fillCircle(r, r, r);
    gfx.lineStyle(2, 0xffffff, 0.9);
    gfx.strokeCircle(r, r, r - 1);
    gfx.generateTexture('proj-bow-poison', PROJ_SIZE, PROJ_SIZE);
    gfx.destroy();
  }

  // Charged special orbs — bigger than the standard bolt. ProjectileView asks
  // for `proj-<type>-green` whenever a shot is the tower's periodic special
  // (the Wizard's green blast, Phantom Hambi's neon ghost), so every tower with
  // a `special` in BALANCE needs one of these too.
  for (const type of ['wizard', 'phantom'] as const) {
    const key = `proj-${type}-green`;
    if (scene.textures.exists(key)) continue;
    const size = 24;
    const r = size / 2;
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x53d769, 1);
    gfx.fillCircle(r, r, r);
    gfx.lineStyle(2, 0xffffff, 0.9);
    gfx.strokeCircle(r, r, r - 1);
    gfx.generateTexture(key, size, size);
    gfx.destroy();
  }

  // Takeout bag (the Restaurant + Storage hero graphic) — a plain paper-brown
  // rounded rectangle so both screens lay out correctly before the real art
  // loads over it.
  {
    const key = 'takeout-normal';
    if (!scene.textures.exists(key)) {
      const size = 192;
      const gfx = scene.add.graphics();
      gfx.fillStyle(0xa9825a, 1);
      gfx.fillRoundedRect(16, 24, size - 32, size - 40, 10);
      gfx.fillStyle(0x8d6a45, 1);
      gfx.fillRect(16, 24, size - 32, 26); // folded-over top
      gfx.lineStyle(3, 0x4a3320, 1);
      gfx.strokeRoundedRect(16, 24, size - 32, size - 40, 10);
      gfx.generateTexture(key, size, size);
      gfx.destroy();
    }
  }

  // -- Rank medals: filled circle with a ring, per tier colour. The rank
  //    number is drawn over the medal in code, never baked into the art. --
  const MEDAL_COLORS: Record<string, number> = {
    'medal-bronze': 0xcd7f32,
    'medal-silver': 0xc0c0c0,
    'medal-gold': 0xf5c518,
    'medal-diamond': 0x7fe3ff,
  };
  for (const [key, color] of Object.entries(MEDAL_COLORS)) {
    if (scene.textures.exists(key)) continue;
    const size = 96;
    const r = size / 2;
    const gfx = scene.add.graphics();
    gfx.fillStyle(color, 1);
    gfx.fillCircle(r, r, r - 4);
    gfx.lineStyle(5, 0xffffff, 0.85);
    gfx.strokeCircle(r, r, r - 7);
    gfx.lineStyle(3, 0x000000, 0.6);
    gfx.strokeCircle(r, r, r - 4);
    gfx.generateTexture(key, size, size);
    gfx.destroy();
  }

  // -- Achievement badges: tier-coloured rounded-diamond so the Achievements
  //    screen and toasts lay out correctly before the generated art loads
  //    over them. [fill, rim, disc] per tier. --
  type Tier = 'bronze' | 'silver' | 'gold' | 'diamond';
  const ACH_TIER_COLORS: Record<Tier, [number, number, number]> = {
    bronze: [0xcd7f32, 0x8a5a1e, 0x5a3a12],
    silver: [0xc0c8d0, 0x7d8894, 0x4d565e],
    gold: [0xf5c518, 0xa8821a, 0x6e5410],
    diamond: [0xbff4ff, 0x2a9bb5, 0x16606f],
  };
  const ACH_KEYS: Record<string, Tier> = {
    'ach-firstkill': 'bronze',
    'ach-stageness': 'bronze',
    'ach-tester': 'bronze',
    'ach-verybossy': 'bronze',
    'ach-shopper': 'bronze',
    'ach-failure': 'bronze',
    'ach-maxtier': 'bronze',
    'ach-gambling': 'bronze',
    'ach-null': 'silver',
    'ach-evolution': 'silver',
    'ach-bewise': 'silver',
    'ach-loadout': 'silver',
    'ach-letitburn': 'silver',
    'ach-aquatic': 'silver',
    'ach-doubles': 'silver',
    'ach-itrustedyou': 'silver',
    'ach-richness': 'silver',
    'ach-theveg': 'gold',
    'ach-shoplifter': 'gold',
    'ach-spicy': 'gold',
    'ach-waver': 'gold',
    'ach-mansion': 'gold',
    'ach-kingdown': 'gold',
    'ach-welcomehub': 'bronze',
    'ach-scientist': 'bronze',
    'ach-angerissues': 'diamond',
    'ach-ko': 'silver',
    'ach-sales': 'silver',
    'ach-chipsociety': 'gold',
    'ach-poisonedluck': 'silver',
    'ach-quester': 'diamond',
    'ach-thestink': 'diamond',
    'ach-mushmillenium': 'diamond',
    'ach-shroombellionfallen': 'diamond',
    'ach-friendlyfungi': 'diamond',
    'ach-ghosting': 'diamond',
    'ach-halohunting': 'diamond',
    'ach-kingoftheboard': 'diamond',
    'ach-gearbox': 'bronze',
  };
  for (const [key, tier] of Object.entries(ACH_KEYS)) {
    if (scene.textures.exists(key)) continue;
    const [fill, rim, disc] = ACH_TIER_COLORS[tier];
    const size = 96;
    const c = size / 2;
    const gfx = scene.add.graphics();
    gfx.fillStyle(fill, 1);
    // Rounded-diamond: a square rotated 45° reads fine at placeholder quality.
    gfx.beginPath();
    gfx.moveTo(c, 6);
    gfx.lineTo(size - 6, c);
    gfx.lineTo(c, size - 6);
    gfx.lineTo(6, c);
    gfx.closePath();
    gfx.fillPath();
    gfx.lineStyle(4, rim, 1);
    gfx.strokePath();
    gfx.fillStyle(disc, 1);
    gfx.fillCircle(c, c, 18);
    gfx.generateTexture(key, size, size);
    gfx.destroy();
  }

  // -- Decals: translucent ground blob in the tower's accent colour --
  const DECAL_TOWERS: TowerType[] = ['cheese', 'bacon', 'sauce'];
  for (const type of DECAL_TOWERS) {
    const key = `decal-${type}`;
    if (scene.textures.exists(key)) continue;
    const r = DECAL_SIZE / 2;
    const gfx = scene.add.graphics();
    gfx.fillStyle(TOWER_COLORS[type], 0.5);
    gfx.fillCircle(r, r, r - 2);
    gfx.generateTexture(key, DECAL_SIZE, DECAL_SIZE);
    gfx.destroy();
  }

  // -- Revision 9 potions: stand-ins until `npm run assets` produces the real
  // PNGs (specced in tools/asset-manifest.ts; any assets/atlas/<key>.png wins).
  if (!scene.textures.exists('potion-samurai')) {
    // A red conical flask with a ribbon band round the neck.
    const gfx = scene.add.graphics();
    gfx.fillStyle(0xb3202a, 1);
    gfx.beginPath();
    gfx.moveTo(40, 12);
    gfx.lineTo(56, 12);
    gfx.lineTo(88, 84);
    gfx.lineTo(8, 84);
    gfx.closePath();
    gfx.fillPath();
    gfx.lineStyle(3, 0x2b0a0c, 1);
    gfx.strokePath();
    gfx.fillStyle(0xff3b47, 1);
    gfx.fillRect(36, 20, 24, 8);
    gfx.generateTexture('potion-samurai', 96, 96);
    gfx.destroy();
  }
  if (!scene.textures.exists('icon-swords')) {
    // Two crossed blades.
    const gfx = scene.add.graphics();
    gfx.lineStyle(8, 0xd9dde3, 1);
    gfx.lineBetween(10, 54, 54, 10);
    gfx.lineBetween(10, 10, 54, 54);
    gfx.lineStyle(6, 0x3a2a1a, 1);
    gfx.lineBetween(10, 54, 20, 44);
    gfx.lineBetween(10, 10, 20, 20);
    gfx.generateTexture('icon-swords', 64, 64);
    gfx.destroy();
  }
  if (!scene.textures.exists('potion-oliveoil')) {
    // A tall bottle of golden-green oil with a cork.
    const gfx = scene.add.graphics();
    gfx.fillStyle(0x9aa83a, 1);
    gfx.fillRoundedRect(30, 26, 36, 62, 8);
    gfx.fillStyle(0xc9b23a, 1);
    gfx.fillRoundedRect(34, 46, 28, 38, 6);
    gfx.fillStyle(0x8b5a2b, 1);
    gfx.fillRect(41, 8, 14, 22);
    gfx.lineStyle(3, 0x2c3a12, 1);
    gfx.strokeRoundedRect(30, 26, 36, 62, 8);
    gfx.generateTexture('potion-oliveoil', 96, 96);
    gfx.destroy();
  }
}
